"use client";

import { useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import { authHref } from "@/lib/jobs/auth-return";
import { isCrossZoneHref } from "@/lib/zones";

/**
 * `onClick` for a "Log in" / "Sign up" link rendered with `authHref(kind,
 * locale, pathname)`, so the visitor also gets their search query back after
 * auth.
 *
 * The rendered `href` is built from the pathname alone, because reading the
 * search params during render (`useSearchParams`) would force a Suspense
 * boundary onto every page that renders the navbar. The query is read from
 * `window.location` at click time instead, which also picks up filters the
 * feed changed client-side after the link rendered. The link kind comes from
 * the anchor's own href, so one handler serves both links.
 */
export function useAuthLinkClick(locale: string, after?: () => void) {
	const router = useRouter();

	return (event: MouseEvent<HTMLAnchorElement>) => {
		after?.();
		if (
			event.defaultPrevented ||
			event.button !== 0 ||
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey
		) {
			return;
		}
		const href = event.currentTarget.getAttribute("href") ?? "";
		const kind = href.startsWith(`/${locale}/signup`)
			? "signup"
			: href.startsWith(`/${locale}/login`)
				? "login"
				: null;
		if (!kind) return;
		const live = authHref(
			kind,
			locale,
			`${window.location.pathname}${window.location.search}`,
		);
		if (live === href) return;
		event.preventDefault();
		if (isCrossZoneHref(live)) window.location.assign(live);
		else router.push(live);
	};
}
